/**
 * The pure per-sibling build-order tree — 05-project-hub-build-order.
 *
 * `work_items.build_order` is scoped to a SIBLING GROUP: the items sharing the
 * same `parent_work_item_id` (`null` = the project's top level). Epics number
 * 1, 2, 3… among top-level siblings; each parent's children number 1, 2, 3…
 * independently; a task's subtasks likewise. The project's OVERALL order is the
 * TREE WALK — top-level siblings in order, then each one's children in order,
 * recursively. This module owns that derivation; every consumer (the Build
 * Order section, the agent's next-step snapshot, the agent live-reorder gate)
 * composes with it rather than re-implementing an order of its own.
 *
 * Within a group the order is the SAME `build_order → rank → id` comparator the
 * flat era used ({@link compareByBuildOrder} from `whatsNext.ts`) — so legacy
 * globally-numbered data (one 1..N across the whole project) still sorts every
 * sibling group correctly and no data migration is needed.
 *
 * Pure (no React, no IO); degrades gracefully on degenerate input — missing
 * parents and parent cycles never throw and never drop an item.
 */

import type { Database } from '../../types/database';
import { compareByBuildOrder } from './whatsNext';

type WorkItemRow = Database['public']['Tables']['work_items']['Row'];
type WorkItemDependencyRow =
  Database['public']['Tables']['work_item_dependencies']['Row'];

/**
 * The minimal shape this module reads off a `work_items` row: the comparator
 * fields plus the tree shape (`parent_work_item_id`) and `type` (so "next up"
 * can skip epics). Accepts a full row or any trimmed object carrying these.
 */
export type BuildOrderTreeItem = Pick<
  WorkItemRow,
  'id' | 'build_order' | 'rank' | 'parent_work_item_id' | 'type'
>;

/** The minimal dependency-edge shape: the blocked item and its blocker. */
export type BuildOrderTreeDependency = Pick<
  WorkItemDependencyRow,
  'work_item_id' | 'depends_on_work_item_id'
>;

/** Defensive shape guard — mirrors `readyToStartItems`'s tolerance of bad rows. */
function wellFormed<T extends BuildOrderTreeItem>(items: ReadonlyArray<T>): T[] {
  return (items ?? []).filter((w): w is T => !!w && typeof w.id === 'string');
}

/**
 * Group the items by `parent_work_item_id` (`null` = top level), each group
 * sorted by the shared `build_order → rank → id` comparator. The group key is
 * the RAW parent id — a child whose parent is absent from the set still groups
 * under that id (the walk appends such unreachable groups; see
 * {@link treeWalkOrder}).
 */
export function siblingGroups<T extends BuildOrderTreeItem>(
  items: ReadonlyArray<T>
): Map<string | null, T[]> {
  const groups = new Map<string | null, T[]>();
  for (const w of wellFormed(items)) {
    const key = typeof w.parent_work_item_id === 'string' ? w.parent_work_item_id : null;
    const list = groups.get(key) ?? [];
    list.push(w);
    groups.set(key, list);
  }
  for (const list of groups.values()) list.sort(compareByBuildOrder);
  return groups;
}

/**
 * The project's overall order: a DFS over {@link siblingGroups} — top-level
 * siblings in order, then each one's children in order, recursively. Items
 * whose parent is missing from the set, or trapped in a parent cycle, are never
 * dropped: every unvisited group is appended afterward (groups in id order,
 * members in comparator order), and the walk never throws.
 */
export function treeWalkOrder<T extends BuildOrderTreeItem>(
  items: ReadonlyArray<T>
): T[] {
  const groups = siblingGroups(items);
  const out: T[] = [];
  const visited = new Set<string>();

  const walk = (parentId: string | null) => {
    const group = groups.get(parentId);
    if (!group) return;
    for (const w of group) {
      if (visited.has(w.id)) continue; // cycle guard
      visited.add(w.id);
      out.push(w);
      walk(w.id);
    }
  };
  walk(null);

  // Unreachable remainder (missing parents / cycles): append, never drop.
  const orphanKeys = [...groups.keys()]
    .filter((k): k is string => k !== null)
    .sort();
  for (const key of orphanKeys) {
    for (const w of groups.get(key) ?? []) {
      if (visited.has(w.id)) continue;
      visited.add(w.id);
      out.push(w);
      walk(w.id);
    }
  }
  return out;
}

/**
 * The per-item readiness rule, exposed as a map instead of a partition: for
 * each item with at least one UNMET blocker, the blocker ids (in first-seen
 * edge order, deduped). EXACTLY `readyToStartItems()`'s judgement — a blocker
 * is unmet iff it is among the passed items; an absent (already-done) blocker
 * is satisfied and produces no entry. An item with no entry is ready.
 */
export function unmetBlockers(
  items: ReadonlyArray<BuildOrderTreeItem>,
  dependencies: ReadonlyArray<BuildOrderTreeDependency>
): Map<string, string[]> {
  const pendingIds = new Set(wellFormed(items).map((w) => w.id));
  const map = new Map<string, string[]>();
  for (const edge of dependencies ?? []) {
    if (!edge) continue;
    const blocked = edge.work_item_id;
    const blocker = edge.depends_on_work_item_id;
    if (typeof blocked !== 'string' || typeof blocker !== 'string') continue;
    if (!pendingIds.has(blocked)) continue;
    if (!pendingIds.has(blocker)) continue; // already-done blocker is satisfied
    const list = map.get(blocked) ?? [];
    if (!list.includes(blocker)) list.push(blocker);
    map.set(blocked, list);
  }
  return map;
}

/**
 * The single actionable "next up" item: the first item in {@link treeWalkOrder}
 * that
 *   (a) is not an epic (an epic is a container of work, never the work itself —
 *       even a childless one),
 *   (b) has no children in the set (a parent's work happens through its
 *       children),
 *   (c) has no unmet blockers, and
 *   (d) has no ancestor with unmet blockers (work inside a blocked epic is not
 *       actionable).
 * Returns `null` when nothing qualifies (empty plan / everything blocked).
 */
export function nextUpId(
  items: ReadonlyArray<BuildOrderTreeItem>,
  dependencies: ReadonlyArray<BuildOrderTreeDependency>
): string | null {
  const shaped = wellFormed(items);
  if (shaped.length === 0) return null;

  const blockers = unmetBlockers(shaped, dependencies);
  const byId = new Map(shaped.map((w) => [w.id, w]));
  const hasChildren = new Set<string>();
  for (const w of shaped) {
    if (typeof w.parent_work_item_id === 'string') hasChildren.add(w.parent_work_item_id);
  }

  /** True when the item or any ancestor (within the set) has unmet blockers. */
  const blockedThroughAncestry = (w: BuildOrderTreeItem): boolean => {
    const seen = new Set<string>();
    let cur: BuildOrderTreeItem | undefined = w;
    while (cur) {
      if (seen.has(cur.id)) return false; // cycle — judged by own blockers only
      seen.add(cur.id);
      if (blockers.has(cur.id)) return true;
      cur =
        typeof cur.parent_work_item_id === 'string'
          ? byId.get(cur.parent_work_item_id)
          : undefined;
    }
    return false;
  };

  for (const w of treeWalkOrder(shaped)) {
    if (w.type === 'epic') continue;
    if (hasChildren.has(w.id)) continue;
    if (blockedThroughAncestry(w)) continue;
    return w.id;
  }
  return null;
}
